'use client';

import type { CSSProperties } from 'react';
import Link from 'next/link';
import { IconArrowRight, IconClock, IconPlugConnected, IconWallet } from '@tabler/icons-react';
import { MINIMUM_BALANCE_BUFFER_HOURS } from '@/lib/integrations/airgpu/pricing';
import { GpuCharacter } from '@/components/shop/homepage/gpu-characters';
import { CYAN } from './arcade-classes';
import { Platform, Reveal, STEP_OBJECTS } from './arcade-art';

const STEPS = [
  {
    title: 'Choose your GPU',
    body: 'Pick a card from the arcade. Each one lists its VRAM, vCPUs, RAM and starting hourly rate, and the configurator shows the exact rate for your region before you create anything.',
    hue: 'var(--chart-4)',
    note: { icon: IconWallet, text: `Starting needs wallet balance for at least ${MINIMUM_BALANCE_BUFFER_HOURS} hours at the machine's rate.` },
  },
  {
    title: 'Configure and launch',
    body: 'Choose the region, Windows image and options, then start the machine. Start-up can take several minutes; the machine panel shows when it is ready.',
    hue: 'var(--chart-5)',
    note: { icon: IconClock, text: 'Start-up usually takes a few minutes.' },
  },
  {
    title: 'Connect and start using it',
    body: 'Connect with Remote Desktop using the IP, username and password the machine panel shows. Stop the machine when you are done.',
    hue: CYAN,
    note: { icon: IconPlugConnected, text: 'Parsec or Moonlight are optional apps you can install yourself.' },
  },
];

/** How it works, as three illustrated power-ups joined by a glowing wire. */
export function GPUPowerUps() {
  return (
    <div>
      <div className="relative">
        {/* the wire joining the three steps */}
        <svg
          aria-hidden
          className="pointer-events-none absolute left-[16%] right-[16%] top-[88px] hidden h-2 w-[68%] md:block"
          viewBox="0 0 100 2"
          preserveAspectRatio="none"
          style={{ '--hue': 'var(--primary)' } as CSSProperties}
        >
          <line x1="0" y1="1" x2="100" y2="1" className="ga-wire lab-dash" strokeWidth="2" strokeDasharray="3 3" vectorEffect="non-scaling-stroke" />
        </svg>

        <ol className="relative grid grid-cols-1 gap-4 md:grid-cols-3 md:gap-6">
          {STEPS.map((step, index) => {
            const Note = step.note.icon;
            return (
              <li key={step.title}>
                <Reveal index={index} className="h-full">
                  <div style={{ '--hue': step.hue } as CSSProperties} className="ga-step relative flex h-full flex-col rounded-2xl p-5 sm:p-6">
                    <div className="flex items-start justify-between gap-3">
                      <span className="ga-step-badge grid size-12 place-items-center rounded-xl font-heading text-2xl font-black">
                        {index + 1}
                      </span>
                      <div aria-hidden className="relative w-28">
                        <div className="bob-a relative z-10">
                          <GpuCharacter persona={STEP_OBJECTS[index]} uid={`arcade-step-${index}`} className="drop-shadow-[0_10px_14px_rgba(0,0,0,0.45)]" />
                        </div>
                        <Platform hue={step.hue} beam={false} className="-mt-[4%] h-5 w-full" />
                      </div>
                    </div>
                    <h3 className="mt-4 font-heading text-xl font-bold text-foreground">{step.title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{step.body}</p>
                    <div className="min-h-3 flex-1" />
                    <p className="mt-4 flex items-start gap-2 rounded-lg bg-background/50 px-3 py-2 text-xs text-foreground">
                      <Note className="mt-0.5 size-3.5 shrink-0 text-[var(--hue)]" />
                      {step.note.text}
                    </p>
                  </div>
                </Reveal>
              </li>
            );
          })}
        </ol>
      </div>

      <Reveal index={3}>
        <div className="mt-5 flex flex-col gap-4 rounded-2xl border border-primary/30 bg-primary/10 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm leading-relaxed text-foreground">
            <span className="font-semibold">How billing works:</span> while a machine runs, usage is metered by the
            started minute at the applicable hourly rate and charged from your wallet, at least hourly and once more
            when you stop it.
          </p>
          <div className="flex shrink-0 gap-4 text-sm font-semibold">
            <Link href="/pricing" className="inline-flex items-center gap-1 text-foreground hover:text-primary">
              Pricing <IconArrowRight className="size-4" />
            </Link>
            <Link href="/how-it-works" className="inline-flex items-center gap-1 text-foreground hover:text-primary">
              Full guide <IconArrowRight className="size-4" />
            </Link>
          </div>
        </div>
      </Reveal>
    </div>
  );
}
